import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@/shared/ui/card";
import { useLanguage } from "@/shared/hooks/use-language";
import { labelOr } from "@/shared/lib/label";
import {
  instrumentTypeLabelKey,
  isInstrumentType,
  type InstrumentType,
} from "@/shared/lib/instrument-type";
import { parsePercentToAmountScale } from "@/shared/lib/amount";
import { consolidationStateOf } from "@/features/portfolio/api/position";
import type { Position, PositionSummary } from "@/features/portfolio/api/position";

interface AllocationDonutProps {
  positions: Position[];
  summary?: PositionSummary | null;
  summaryUnavailable?: boolean;
}

const COLOR_BY_INSTRUMENT_TYPE: Record<InstrumentType, string> = {
  debt: "hsl(var(--chart-1))",
  equity: "hsl(var(--chart-2))",
  currency: "hsl(var(--chart-3))",
  crypto: "hsl(var(--chart-4))",
  fund: "hsl(var(--chart-5))",
  option: "hsl(var(--chart-6))",
};
const OTHER_COLOR = "hsl(var(--muted-foreground))";
const SURFACE = "hsl(var(--card))";

export const AllocationDonut = ({ positions, summary, summaryUnavailable }: AllocationDonutProps) => {
  const { t } = useLanguage();

  // A summary that never answered is not a summary that answered with nothing: the
  // "prices are on their way" copy below is a claim about market-data coverage, and the
  // page states the failure — with a retry — in its own card instead.
  if (summaryUnavailable) return null;

  // A weight the client cannot read is an omitted slice, never a zero-size one. The
  // backend emits `null` for a share of a zero whole, and `percent_string` carries no
  // magnitude bound, so `parseFloat(...) || 0` drew an invisible wedge with an empty
  // label for a fact the reader was entitled to be told nothing about. A donut of the
  // slices that could be read is honest; a donut with a phantom slice in it is not.
  const readableAllocations = (summary?.allocations || []).flatMap((allocation) => {
    const share = parsePercentToAmountScale(allocation.weight);
    if (share == null) return [];
    return [{ instrumentType: allocation.instrument_type, weightLabel: allocation.weight, share }];
  });
  if (readableAllocations.length === 0) {
    // Buckets with no readable share at all. The backend emits a bucket only once the
    // consolidated NAV resolves, so this is the fully-netting book: every share is a
    // share of zero, which is undefined rather than pending. Saying prices are on their
    // way here would name an outage that is not happening.
    if ((summary?.allocations || []).length > 0) {
      return (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="allocation-unavailable">
          {t("portfolio.allocationZeroNav")}
        </Card>
      );
    }
    if (positions.length === 0) return null;
    // Only a rate outage earns the exchange-rate copy: the same null also means nothing was
    // priced, and blaming FX for a pricing gap misdescribes the outage to the reader.
    const isFxUnavailable = consolidationStateOf(summary) === "rate-missing";
    return (
      <Card className="p-6 text-sm text-muted-foreground" data-testid="allocation-unavailable">
        {isFxUnavailable
          ? t("portfolio.allocationFxUnavailable")
          : t("portfolio.allocationPending")}
      </Card>
    );
  }

  const data = readableAllocations.map((allocation) => ({
    name: allocation.instrumentType,
    label: labelOr(t, instrumentTypeLabelKey(allocation.instrumentType), allocation.instrumentType),
    weightLabel: allocation.weightLabel,
    // The only place a share becomes a float, because a wedge angle is geometry rather
    // than a figure anyone reads. What the reader reads is `weightLabel`, the server's
    // own string, unchanged.
    value: Number(allocation.share.amount) / 10 ** allocation.share.scale,
    color: isInstrumentType(allocation.instrumentType)
      ? COLOR_BY_INSTRUMENT_TYPE[allocation.instrumentType]
      : OTHER_COLOR,
  }));

  return (
    <Card className="p-6">
      <h3 className="font-semibold mb-4">{t("portfolio.allocationTitle")}</h3>
      <div className="flex flex-wrap items-center gap-8">
        <div className="w-48 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius="60%"
                outerRadius="100%"
                stroke={SURFACE}
                strokeWidth={2}
                isAnimationActive={false}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(_value: number, _name: string, item: { payload?: { weightLabel?: string } }) =>
                  item?.payload?.weightLabel ?? ""
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-2" data-testid="allocation-legend">
          {data.map((entry) => (
            <li key={entry.name} className="flex items-center gap-2 text-sm">
              <span
                aria-hidden="true"
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-foreground" data-testid="allocation-legend-label">{entry.label}</span>
              <span className="text-muted-foreground tabular-nums">{entry.weightLabel}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};
